import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/api';

const genUuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Step-wise draft autosave for the /forms wizards.
//
// On mount (unless `mode === 'edit'`, which is editing an already-submitted
// row) this assigns/reuses a `draft_id`, stamps it + `mode=draft` into the
// URL so a refresh lands back on the same draft. It watches `step` and every
// value in `watch` and autosaves when any of them changes: an immediate save
// on step transitions, a debounced save otherwise (so a refresh mid-step,
// before the user ever reaches Next, doesn't lose typed data).
//
// `watch` should list every piece of state `buildSnapshot` reads — usually
// just `[formData]`, but forms that keep some data outside `formData` (e.g.
// AIF's attachment/signature state, which lives in separate useState calls)
// need those included too, or edits to them won't trigger a save at all.
//
// Call `notifyLoaded()` right before applying a loaded draft's data (i.e. at
// the top of the `if (draft) { ... }` block in each form's `loadDraftData`).
// Without it, restoring a draft's `step`/watched state would itself trigger
// these same watchers and fire a redundant save immediately after the load.
export const useFormDraft = ({ formName, mode, step, watch, buildSnapshot }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const draftEligible = mode !== 'edit';

  const [draftId] = useState(() => {
    if (!draftEligible) return null;
    return searchParams.get('draft_id') || genUuid();
  });

  useEffect(() => {
    if (!draftEligible || !draftId) return;
    if (searchParams.get('draft_id') === draftId && searchParams.get('mode') === 'draft') return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('draft_id', draftId);
        next.set('mode', 'draft');
        return next;
      },
      { replace: true }
    );
    // Only ever needs to run once, to stamp the freshly generated draftId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // buildSnapshot closes over each render's formData/step, so it's a new
  // function every render. Reading it via a ref (rather than depending on it
  // directly) keeps `saveDraft` itself referentially stable and means every
  // invocation always reads the latest form state instead of whatever was
  // current when that particular call started.
  const buildSnapshotRef = useRef(buildSnapshot);
  buildSnapshotRef.current = buildSnapshot;

  const savingRef = useRef(false);
  const pendingRef = useRef(false);

  const saveDraft = useCallback(async () => {
    if (!draftEligible || !draftId) return;
    if (savingRef.current) {
      pendingRef.current = true;
      return;
    }
    savingRef.current = true;
    try {
      const snapshot = await buildSnapshotRef.current();
      const fd = new FormData();
      fd.append('draft_id', draftId);
      fd.append('form_name', formName);
      fd.append('form_data', JSON.stringify(snapshot));
      fd.append('status', 'pending');
      fd.append('updated_at', new Date().toISOString());
      await api.post('/v1/forms/drafts/upsert', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (error) {
      console.error('Draft autosave failed:', error);
    } finally {
      savingRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        saveDraft();
      }
    }
  }, [draftEligible, draftId, formName]);

  const clearDraft = useCallback(async () => {
    if (!draftEligible || !draftId) return;
    try {
      await api.post('/v1/forms/drafts/delete', { draft_id: draftId });
    } catch (error) {
      console.error('Draft cleanup failed:', error);
    }
  }, [draftEligible, draftId]);

  const debounceRef = useRef(null);
  const scheduleSave = useCallback(
    (delay = 1000) => {
      if (!draftEligible || !draftId) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        saveDraft();
      }, delay);
    },
    [draftEligible, draftId, saveDraft]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  // Loading a draft calls setFormData/setCurrentStep, which — without this
  // guard — would immediately trigger the watchers below and re-save the
  // data we just loaded. `notifyLoaded()` suppresses exactly one pass of
  // both watchers; the cleanup effect (no deps, runs after every render)
  // then clears the suppression once both have had a chance to see it.
  const suppressRef = useRef(false);
  const notifyLoaded = useCallback(() => {
    suppressRef.current = true;
  }, []);

  // Compare against the last value this effect actually processed, rather
  // than a boolean "is this the first render" ref. A boolean flag breaks
  // under React.StrictMode (active in this app's index.js): StrictMode
  // double-invokes effects on mount, and a plain ref mutation from the first
  // synthetic invocation persists into the second, making the second
  // invocation think it's a real subsequent render — firing a premature save
  // with the still-default, pre-load form state. Comparing against the last
  // *processed* value is invocation-count-agnostic: both synthetic mount
  // invocations see the same unchanged value and correctly no-op.
  const lastStepRef = useRef(step);
  useEffect(() => {
    if (step === lastStepRef.current) return;
    lastStepRef.current = step;
    if (suppressRef.current) return;
    saveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const lastWatchRef = useRef(watch);
  useEffect(() => {
    const prev = lastWatchRef.current;
    const changed = watch.length !== prev.length || watch.some((v, i) => v !== prev[i]);
    lastWatchRef.current = watch;
    if (!changed) return;
    if (suppressRef.current) return;
    scheduleSave();
    // watch's own contents drive this effect at runtime; its length is
    // constant per form instance, so this is safe despite not being a
    // static literal the exhaustive-deps rule can verify.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, watch);

  useEffect(() => {
    suppressRef.current = false;
  });

  return { draftId, saveDraft, scheduleSave, clearDraft, notifyLoaded };
};
